import React from "react";

/**
 * Signup hero "benefits" (value props) — the short list shown in the branded
 * hero panel of the public signup. Venue-configurable via `venue.signup_benefits`
 * (a list of { icon, label }); when unset the built-in defaults render, so a
 * venue that never touches this looks exactly as before.
 *
 * The icon set + defaults live here so the public signup and the admin Branding
 * editor share one source of truth and can't drift.
 */

export const DEFAULT_BENEFITS = [
  { icon: "lock", label: "Secure, encrypted checkout" },
  { icon: "clock", label: "Takes about two minutes" },
  { icon: "star", label: "Members-only rewards & offers" },
];

const S = { fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };

/* Curated icon set. Keys are stored in the DB; keep them stable. */
const PATHS = {
  lock:     <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  shield:   <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
  clock:    <><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></>,
  bolt:     <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></>,
  star:     <><polygon points="12 2 15 8.5 22 9.3 17 14 18.2 21 12 17.6 5.8 21 7 14 2 9.3 9 8.5 12 2" /></>,
  gift:     <><polyline points="20 12 20 22 4 22 4 12" /><rect x="2" y="7" width="20" height="5" /><line x1="12" y1="22" x2="12" y2="7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></>,
  card:     <><rect x="2" y="4" width="20" height="16" rx="2.5" /><circle cx="8.5" cy="10" r="2" /><path d="M5 16a3.5 3.5 0 0 1 7 0" /><line x1="15" y1="9" x2="19" y2="9" /><line x1="15" y1="13" x2="19" y2="13" /></>,
  check:    <><circle cx="12" cy="12" r="10" /><polyline points="8.5 12.5 11 15 16 9.5" /></>,
  users:    <><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>,
  tag:      <><path d="M20.6 13.4 11 3.8A2 2 0 0 0 9.6 3.2H4a2 2 0 0 0-2 2v5.6a2 2 0 0 0 .6 1.4L12.2 22a2 2 0 0 0 2.8 0l5.6-5.6a2 2 0 0 0 0-2.8z" /><line x1="7" y1="7" x2="7.01" y2="7" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  heart:    <><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21.2l7.8-7.7 1-1.1a5.5 5.5 0 0 0 0-7.8z" /></>,
};

/** Options for the icon picker (stable key + friendly label). */
export const BENEFIT_ICON_OPTIONS = [
  { value: "lock", label: "Lock" },
  { value: "shield", label: "Shield" },
  { value: "clock", label: "Clock" },
  { value: "bolt", label: "Lightning" },
  { value: "star", label: "Star" },
  { value: "gift", label: "Gift" },
  { value: "card", label: "Membership card" },
  { value: "check", label: "Check" },
  { value: "users", label: "Members" },
  { value: "tag", label: "Tag / offer" },
  { value: "calendar", label: "Calendar" },
  { value: "heart", label: "Heart" },
];

/** Render one benefit icon by key. Falls back to a check if the key is unknown. */
export function BenefitIcon({ name, size = 16 }) {
  const body = PATHS[name] || PATHS.check;
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} {...S} aria-hidden="true">{body}</svg>
  );
}

/** The benefits to render for a venue: its configured list, else the defaults. */
export function resolveBenefits(venue) {
  const list = venue && Array.isArray(venue.signup_benefits) ? venue.signup_benefits : null;
  const cleaned = (list || [])
    .filter((b) => b && typeof b.label === "string" && b.label.trim())
    .map((b) => ({ icon: b.icon || "check", label: b.label.trim() }));
  return cleaned.length ? cleaned : DEFAULT_BENEFITS;
}
